import {
  getDocumentCollectionPageModel,
  initiateUpload as initiateUploadHandler,
  completeUpload as completeUploadHandler,
  submitHarHabituchDetails as submitHarHabituchDetailsHandler,
  sendDocCollectionCode as sendDocCollectionCodeHandler,
  verifyDocCollectionCode as verifyDocCollectionCodeHandler,
} from '../../../utils/apiHandlers';
import {MOCK_PAGE_MODEL, MOCK_STATUS_RESPONSE, MOCK_ALL_APPROVED, MOCK_AUTH_PAGE_MODEL} from './mockData';

const {REACT_APP_NODE_ENV, REACT_APP_MOCK_DOCUMENTS, REACT_APP_MOCK_AUTH} = process.env;
const useMocks = REACT_APP_NODE_ENV === 'local' && REACT_APP_MOCK_DOCUMENTS !== 'false';
const useMockAuth = useMocks && REACT_APP_MOCK_AUTH === 'true';
let mockAuthCompleted = false;
export {useMocks};

/**
 * Fetch page model for document collection.
 */
export const fetchPageModel = async (shortLinkKey) => {
  if (useMocks) {
    if (useMockAuth && !mockAuthCompleted) {
      return MOCK_AUTH_PAGE_MODEL;
    }
    return MOCK_PAGE_MODEL;
  }
  const response = await getDocumentCollectionPageModel(shortLinkKey);
  return response.data;
};

/**
 * Mark mock auth as completed (called after successful verify in mock mode).
 */
export const completeMockAuth = () => {
  mockAuthCompleted = true;
};

/**
 * Get current document statuses (re-fetches page model).
 */
export const getDocumentStatuses = async (shortLinkKey) => {
  if (useMocks) {
    return MOCK_STATUS_RESPONSE;
  }
  const response = await getDocumentCollectionPageModel(shortLinkKey);
  return {statuses: response.data.statuses};
};

/**
 * Initiate a presigned-URL upload (single file).
 * Wraps the file in the `files` array format expected by the backend,
 * then unwraps the first upload entry for the caller.
 * @param {string} shortLinkKey
 * @param {string} documentType - e.g. 'license', 'protection', 'insurance_history_1'
 * @param {File} file
 * @returns {{ uploadUrl, uploadId }}
 */
export const initiateUpload = async (shortLinkKey, documentType, file) => {
  if (useMocks) {
    await new Promise((resolve) => setTimeout(resolve, 500));
    return {uploadUrl: null, uploadId: 'mock-upload-id'};
  }
  const response = await initiateUploadHandler(shortLinkKey, {
    documentType,
    files: [{filename: file.name, contentType: file.type, fileSize: file.size}],
  });
  const {uploads} = response.data;
  return {uploadUrl: uploads[0].uploadUrl, uploadId: uploads[0].uploadId};
};

/**
 * Initiate a batch upload (multiple files in one call).
 * Used for insurance_history slots where multiple files are sent together.
 * @param {string} shortLinkKey
 * @param {string} documentType - e.g. 'insurance_history_1'
 * @param {File[]} files - array of File objects
 * @returns {{ groupId, uploads: Array<{ uploadId, uploadUrl, requiredHeaders, expiry }> }}
 */
export const initiateBatchUpload = async (shortLinkKey, documentType, files) => {
  if (useMocks) {
    await new Promise((resolve) => setTimeout(resolve, 500));
    return {
      groupId: 'mock-group-id',
      uploads: files.map((f, i) => ({
        uploadId: `mock-upload-id-${i}`,
        uploadUrl: null,
        requiredHeaders: {'Content-Type': f.type},
        expiry: new Date(Date.now() + 300000).toISOString(),
      })),
    };
  }
  const response = await initiateUploadHandler(shortLinkKey, {
    documentType,
    files: files.map((f) => ({
      filename: f.name,
      contentType: f.type,
      fileSize: f.size,
    })),
  });
  return response.data;
};

/**
 * Upload file to presigned S3 URL. In mock mode simulates the upload.
 * @param {string|null} uploadUrl
 * @param {File} file
 */
export const uploadFileToS3 = async (uploadUrl, file) => {
  if (useMocks || !uploadUrl) {
    await new Promise((resolve) => setTimeout(resolve, 800));
    return;
  }
  await fetch(uploadUrl, {
    method: 'PUT',
    body: file,
    headers: {'Content-Type': file.type},
  });
};

/**
 * Complete upload after file is uploaded to presigned URL.
 * @param {string} shortLinkKey
 * @param {string} uploadId
 */
export const completeUpload = async (shortLinkKey, uploadId) => {
  if (useMocks) {
    await new Promise((resolve) => setTimeout(resolve, 300));
    return {success: true};
  }
  const response = await completeUploadHandler(shortLinkKey, uploadId);
  return response.data;
};

/**
 * Submit Har HaBituch details for insurance history verification.
 * @param {string} shortLinkKey
 * @param {{ idIssuanceDate: string, dateOfBirth: string, idNumber: string, carLicenseNumber: string }} details
 */
export const submitHarHabituchDetails = async (shortLinkKey, details) => {
  if (useMocks) {
    return new Promise((resolve) => {
      setTimeout(() => resolve({success: true}), 800);
    });
  }
  const response = await submitHarHabituchDetailsHandler(shortLinkKey, details);
  if (response.success === false) {
    throw new Error(response.message || 'SUBMIT_FAILED');
  }
  return response.data;
};

/**
 * Send OTP code for document collection authentication.
 * @param {string} shortLinkKey
 * @param {string} method - 'SMS' or 'WHATSAPP'
 */
export const sendCode = async (shortLinkKey, method) => {
  if (useMocks) {
    await new Promise((resolve) => setTimeout(resolve, 500));
    return {status: 'SENT'};
  }
  const response = await sendDocCollectionCodeHandler(shortLinkKey, method);
  return response.data;
};

/**
 * Verify OTP code for document collection authentication.
 * @param {string} shortLinkKey
 * @param {string} code - 6-digit code
 */
export const verifyCode = async (shortLinkKey, code) => {
  if (useMocks) {
    await new Promise((resolve) => setTimeout(resolve, 800));
    return {authStatus: 'AUTHENTICATED', expiresAt: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString()};
  }
  const response = await verifyDocCollectionCodeHandler(shortLinkKey, code);
  return response.data;
};
